'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { ImagePlus, Link2, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ipfsToHttp } from '@/lib/ipfs';

interface ImageFilePickerProps {
  /** Current value as a URL (https / ipfs / gateway). Empty or null = no image. */
  value: string | null;
  /** Called with the new gateway URL after upload, or `null` when cleared. */
  onChange: (url: string | null) => void;
  /** Square preview edge, px. Defaults to 88. */
  size?: number;
  /** Pinata label ("board" / "adapter" etc.) - stored in pin metadata. */
  contextLabel?: string;
  /** Show a collapsed "paste URL instead" escape hatch for power users. */
  allowPasteUrl?: boolean;
  /** Disable the picker entirely. */
  disabled?: boolean;
  className?: string;
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Drag-drop + click-to-browse image uploader backed by
 * `POST /api/ipfs/upload-image`. Replaces the bare "Image URL" text input
 * used throughout the creator tools and adapter copy editors.
 *
 * The component owns the upload lifecycle but stores no URL state itself -
 * consumers keep the URL and pass it back via `value` so the wider form can
 * still submit through whatever `saveBoardMeta` / `saveAdapterNote` flow it
 * already uses. Clearing calls `onChange(null)` so the consumer knows to
 * drop the value (we use empty-string sentinels for "no image" elsewhere).
 */
export function ImageFilePicker({
  value,
  onChange,
  size = 88,
  contextLabel = 'image',
  allowPasteUrl = false,
  disabled = false,
  className,
}: ImageFilePickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteInput, setPasteInput] = useState('');
  // Instant, gateway-independent preview of the just-picked file. The
  // remote `value` only becomes the IPFS gateway URL *after* the upload
  // round-trip, and a freshly-pinned CID isn't always servable for
  // display yet — so relying on it alone left the thumbnail blank even
  // though selection + upload had succeeded ("image never fills"). A
  // local `blob:` URL fills the moment a file is chosen and we revoke it
  // on replace / clear / unmount to avoid leaks.
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const revokeLocalPreview = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => revokeLocalPreview(), [revokeLocalPreview]);

  // Prefer the local blob once a file is picked this session; otherwise
  // fall back to the stored value (existing image on mount, pasted URL).
  const remotePreview = value ? ipfsToHttp(value) ?? value : null;
  const previewUrl = localPreview ?? remotePreview;

  const uploadFile = useCallback(
    async (file: File) => {
      if (file.size > MAX_BYTES) {
        setError('Image is over 5 MB');
        return;
      }
      if (!/^image\//.test(file.type)) {
        setError('File must be an image');
        return;
      }
      setError(null);
      // Fill the thumbnail immediately from the local file, before the
      // upload completes — decoupled from IPFS gateway availability.
      revokeLocalPreview();
      const localUrl = URL.createObjectURL(file);
      objectUrlRef.current = localUrl;
      setLocalPreview(localUrl);
      setBusy(true);
      try {
        const base64 = await fileToDataUrl(file);
        const res = await fetch('/api/ipfs/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Image: base64,
            contextLabel,
          }),
        });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as {
            error?: string;
            message?: string;
          };
          throw new Error(j.error || j.message || `Upload failed (${res.status})`);
        }
        const json = (await res.json()) as { imageUrl: string };
        if (!json.imageUrl) throw new Error('Upload returned no URL');
        onChange(json.imageUrl);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed');
        // Drop the optimistic preview so a failed upload doesn't read as
        // a success (the consumer's `value` was never set).
        revokeLocalPreview();
        setLocalPreview(null);
      } finally {
        setBusy(false);
      }
    },
    [contextLabel, onChange, revokeLocalPreview],
  );

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) void uploadFile(file);
    },
    [uploadFile],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const file = event.dataTransfer.files?.[0];
      if (file) void uploadFile(file);
    },
    [disabled, uploadFile],
  );

  const clear = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      revokeLocalPreview();
      setLocalPreview(null);
      onChange(null);
      setPasteInput('');
      setError(null);
      if (inputRef.current) inputRef.current.value = '';
    },
    [onChange, revokeLocalPreview],
  );

  const onPasteSubmit = useCallback(() => {
    const trimmed = pasteInput.trim();
    if (!trimmed) return;
    // A pasted URL replaces any locally-picked file preview.
    revokeLocalPreview();
    setLocalPreview(null);
    onChange(trimmed);
    setPasteInput('');
    setPasteOpen(false);
  }, [onChange, pasteInput, revokeLocalPreview]);

  const previewSize = `${size}px`;
  const inputId = `image-file-picker-${contextLabel}`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-start gap-2">
        <label
          htmlFor={inputId}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{ width: previewSize, height: previewSize }}
          className={cn(
            'group relative grid shrink-0 cursor-pointer place-items-center overflow-hidden rounded-md border border-dashed border-amber-400/30 bg-black/50',
            'transition-colors hover:border-amber-300/60 hover:bg-amber-400/5',
            previewUrl && 'border-solid border-amber-300/45',
            dragOver && 'border-amber-300/80 bg-amber-400/10',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          {previewUrl ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={previewUrl}
                alt="Selected image preview"
                className="h-full w-full object-cover"
              />
              {busy && (
                <div className="absolute inset-0 grid place-items-center bg-black/60">
                  <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
                </div>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={clear}
                  aria-label="Remove image"
                  className={cn(
                    'absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full',
                    'bg-black/80 text-amber-100/80 opacity-0 transition-opacity',
                    'group-hover:opacity-100 hover:text-amber-50',
                  )}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </>
          ) : busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
          ) : (
            <span className="flex flex-col items-center gap-1 text-amber-100/60 transition-colors group-hover:text-amber-50">
              <ImagePlus className="h-5 w-5" strokeWidth={1.6} />
              <span className="font-mono text-[8px] uppercase tracking-[0.18em]">
                Upload
              </span>
            </span>
          )}
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={ACCEPT}
            onChange={onFileChange}
            disabled={disabled || busy}
            className="sr-only"
          />
        </label>

        <div className="flex min-w-0 flex-col gap-1 pt-0.5">
          <p className="text-[10px] text-zinc-400 leading-snug">
            PNG, JPG, WebP, GIF or SVG. Up to 5 MB. Dropped in seconds.
          </p>
          {allowPasteUrl && (
            <button
              type="button"
              onClick={() => setPasteOpen((o) => !o)}
              className="self-start inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.16em] text-zinc-500 hover:text-amber-200/90"
            >
              <Link2 className="h-2.5 w-2.5" />
              {pasteOpen ? 'Hide URL input' : 'Paste URL instead'}
            </button>
          )}
        </div>
      </div>

      {pasteOpen && allowPasteUrl && (
        <div className="flex gap-1.5">
          <input
            type="url"
            value={pasteInput}
            onChange={(e) => setPasteInput(e.target.value)}
            placeholder="https://... or ipfs://..."
            className="flex-1 h-7 rounded border border-white/10 bg-black/40 px-2 text-[11px] text-amber-50/95 placeholder:text-zinc-600 focus:border-amber-400/50 focus:outline-none"
          />
          <button
            type="button"
            onClick={onPasteSubmit}
            disabled={!pasteInput.trim()}
            className="h-7 px-2 rounded border border-amber-400/30 bg-amber-500/15 text-[10px] font-mono uppercase tracking-wide text-amber-100 hover:bg-amber-500/25 disabled:opacity-50"
          >
            Set
          </button>
        </div>
      )}

      {error && <p className="text-[10px] text-rose-300/90">{error}</p>}
    </div>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read error'));
    reader.readAsDataURL(file);
  });
}
